import getUserByCcUserId from '../api/users/getUserByCcUserId';
import ServiceUser from '../types/ServiceUser';
import { serviceState } from './state';

const inFlight = new Map<string, Promise<ServiceUser | null>>();

async function fetchAndUpsert(
  contactCenterUserId: string,
): Promise<ServiceUser | null> {
  const fresh = await getUserByCcUserId(contactCenterUserId);
  if (!fresh) return null;

  const existing =
    serviceState.userByContactCenterUserId[contactCenterUserId] ?? null;
  const merged: ServiceUser = existing
    ? {
        ...fresh,
        stats: fresh.stats ?? existing.stats,
        activityStatus: existing.activityStatus,
        availabilityStatus: existing.availabilityStatus,
      }
    : fresh;

  const index = serviceState.users.findIndex(
    (u) => u.contactCenterUserId === contactCenterUserId,
  );
  const next = [...serviceState.users];
  if (index >= 0) {
    next[index] = merged;
  } else {
    next.push(merged);
  }
  // reassign the full array so state.ts subscribe rebuilds userById/userByContactCenterUserId
  serviceState.users = next;

  return merged;
}

/**
 * Fetch one user by `contactCenterUserId` and upsert into `serviceState.users`,
 * preserving any existing `activityStatus`, `availabilityStatus`, and `stats`
 * from the in-memory record. Concurrent calls for the same id share a single
 * network request via an in-flight dedup map. Returns null when no user matches.
 */
export default function refreshUser(
  contactCenterUserId: string,
): Promise<ServiceUser | null> {
  const pending = inFlight.get(contactCenterUserId);

  if (pending) return pending;

  const p = fetchAndUpsert(contactCenterUserId).finally(() => {
    inFlight.delete(contactCenterUserId);
  });

  inFlight.set(contactCenterUserId, p);

  return p;
}
